import { match, registerElement } from '@/utilities/mvu-custom-element'

type Model = { index: number; length: number }

type Direction = 'left' | 'right'
type Msg = { SetIndex: number } | { Next: null } | { Prev: null }

registerElement<Model, Msg>('image-carousel', {
  init: el => ({
    model: {
      index: 0,
      length: el.querySelector('[data-slides]')?.querySelectorAll('img').length ?? 0
    },
    cmd: null
  }),
  update: (msg, model) =>
    match(msg, {
      SetIndex: i => ({ model: { ...model, index: i }, cmd: null }),
      Prev: () => ({
        model: {
          ...model,
          index: (model.index - (1 % model.length) + model.length) % model.length
        },
        cmd: null
      }),
      Next: () => ({ model: { ...model, index: (model.index + 1) % model.length }, cmd: null })
    }),
  subscriptions: () => (el, cb) => {
    const ctrl = new AbortController()
    const slides = el.querySelector('[data-slides]')
    if (slides) {
      onSwipe(slides, direction => cb(direction === 'right' ? { Prev: null } : { Next: null }), {
        signal: ctrl.signal
      })
    }

    return () => ctrl.abort()
  },
  view: model => (el, send) => {
    el
      .querySelector('[data-slides]')
      ?.querySelectorAll('img')
      .forEach((el, i) => el.classList.toggle('opacity-0', i !== model.index))

    // Handle click preview thumbnail
    el.querySelectorAll<HTMLElement>('[data-preview]').forEach((el, i) => {
      el.onclick = () => send({ SetIndex: i })

      const isActive = i === model.index

      el.classList.toggle('border-breeze-blue', isActive)
      el.classList.toggle('border-2', isActive)

      if (isActive) {
        el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
      }
    })

    // Arrow buttons
    const next = el.querySelector<HTMLElement>('[data-next]')
    if (next) {
      next.onclick = e => {
        e.preventDefault()
        send({ Next: null })
      }
    }
    const prev = el.querySelector<HTMLElement>('[data-prev]')
    if (prev) {
      prev.onclick = e => {
        e.preventDefault()
        send({ Prev: null })
      }
    }
  }
})

function onSwipe(
  el: Element,
  cb: (direction: Direction) => void,
  { signal, threshold = 50 }: { signal?: AbortSignal; threshold?: number } = {}
) {
  let start: Touch

  el.addEventListener('touchstart', (e: TouchEvent) => (start = e.changedTouches[0]), {
    signal,
    passive: true
  })

  el.addEventListener(
    'touchmove',
    (e: TouchEvent) => {
      const touch = e.changedTouches[0]
      const isHorizontal =
        Math.abs(touch.screenX - start.screenX) > Math.abs(touch.screenY - start.screenY)

      // Prevent horizontal scrolling
      if (isHorizontal) {
        e.preventDefault()
        e.stopPropagation()
        return false
      }
    },
    {
      signal,
      passive: true
    }
  )

  el.addEventListener(
    'touchend',
    (e: TouchEvent) => {
      const touch = e.changedTouches[0]

      if (Math.abs(touch.screenX - start.screenX) < threshold) return

      cb(touch.screenX < start.screenX ? 'left' : 'right')
    },
    { signal }
  )
}
